import emojiRegex from "emoji-regex";
import { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  formatCustomerAddress,
  pickCustomerPhone,
  type CustomerAddress,
} from "@/api/endpoints/address";
import type { Location as MasterLocation } from "@/api/endpoints/locations";
import { goBack, useBackGuard } from "@/app/back";
import { ROUTES } from "@/app/routes";
import { LocationSearchField } from "@/components/ui/LocationSearchField";
import { ScreenHeader } from "@/components/ui/ScreenHeader";
import {
  applyLocation as applyLocationToForm,
  buildNextAddress,
  EMPTY_ADDRESS_FORM,
  type AddressFormState,
} from "./addressForm";
import type { ChangeAddressConfirmState } from "./ChangeAddressConfirmPage";

export type { AddressFormState };

export interface ChangeAddressLocationState {
  current: CustomerAddress;
  form?: AddressFormState;
}


const REQUIRED_ERROR = "กรุณากรอกข้อมูล";
const SPECIAL_CHARS_REGEX = /[!@#$%?]/;
const SPECIAL_CHARS_ERROR = "ไม่สามารถใช้อักขระพิเศษได้ ! , @ , # , $ , % , ?";

/** Emoji count as unsupported characters too, alongside the six marks above. */
function hasSpecialChars(value: string): boolean {
  return SPECIAL_CHARS_REGEX.test(value) || emojiRegex().test(value);
}

export default function ChangeAddressPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const entry = location.state as ChangeAddressLocationState | null;
  const current = entry?.current;

  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState<AddressFormState>(
    () => entry?.form ?? EMPTY_ADDRESS_FORM,
  );
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);
  const leavingRef = useRef(false);
  const [touched, setTouched] = useState<Partial<Record<keyof AddressFormState, boolean>>>(
    {},
  );
  const [blurred, setBlurred] = useState<Partial<Record<keyof AddressFormState, boolean>>>(
    {},
  );
  const markTouched = (key: keyof AddressFormState) => {
    setTouched((prev) => (prev[key] === true ? prev : { ...prev, [key]: true }));
  };
  const markBlurred = (key: keyof AddressFormState) => {
    markTouched(key);
    setBlurred((prev) => (prev[key] === true ? prev : { ...prev, [key]: true }));
  };

  const dirty = Object.values(form).some((value) => value.trim().length > 0);

  useBackGuard(() => {
    if (!dirty || leavingRef.current) {
      return false;
    }
    setShowLeaveWarning(true);
    return true;
  });

  const houseNoTrim = form.houseNo.trim();
  const houseNoError =
    houseNoTrim.length === 0
      ? REQUIRED_ERROR
      : hasSpecialChars(form.houseNo)
        ? SPECIAL_CHARS_ERROR
        : houseNoTrim.startsWith("0")
          ? "บ้านเลขที่ไม่สามารถขึ้นต้นด้วย 0 ได้"
          : "";

  const villageError =
    form.village.length > 0 && hasSpecialChars(form.village)
      ? SPECIAL_CHARS_ERROR
      : "";

  const mooTrim = form.moo.trim();
  const mooError =
    mooTrim.length === 0
      ? REQUIRED_ERROR
      : mooTrim === "0"
        ? "หมู่ที่ไม่สามารถเป็น 0 ได้"
        : /^0./.test(mooTrim)
          ? "ต้องเป็นตัวเลข 0 ถึง 9 หรือ - เท่านั้น"
          : "";

  const roadTrim = form.road.trim();
  const roadError =
    roadTrim.length === 0
      ? REQUIRED_ERROR
      : hasSpecialChars(form.road)
        ? SPECIAL_CHARS_ERROR
        : "";

  const soiError =
    form.soi.length > 0 && hasSpecialChars(form.soi)
      ? SPECIAL_CHARS_ERROR
      : "";

  const phoneRequiredError = form.phone.length === 0 ? REQUIRED_ERROR : "";
  const phoneLengthError =
    form.phone.length > 0 && form.phone.length < 9
      ? "กรุณากรอกเบอร์โทรศัพท์ให้ครบ 9-10 หลัก"
      : "";
  const phoneError = phoneRequiredError.length > 0 ? phoneRequiredError : phoneLengthError;
  const shownPhoneError =
    touched.phone === true && phoneRequiredError.length > 0
      ? phoneRequiredError
      : blurred.phone === true
        ? phoneLengthError
        : "";

  const canContinue =
    (
      [
        "houseNo",
        "moo",
        "road",
        "province",
        "district",
        "subdistrict",
        "postcode",
        "phone",
      ] as (keyof AddressFormState)[]
    ).every((key) => form[key].trim().length > 0) &&
    houseNoError.length === 0 &&
    villageError.length === 0 &&
    mooError.length === 0 &&
    roadError.length === 0 &&
    soiError.length === 0 &&
    phoneError.length === 0;

  const setField = (key: keyof AddressFormState, value: string) => {
    markTouched(key);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const applyLocation = (master: MasterLocation) => {
    setForm((prev) => applyLocationToForm(prev, master));
  };

  return (
    <>
      <ScreenHeader title="กรอกที่อยู่ใหม่" />
      <main className="flex-1 bg-white px-4 pt-5 pb-[calc(120px+env(safe-area-inset-bottom))] tracking-[-0.011em]">
        {current === undefined ? (
          <p className="text-14 text-field-label mt-2 leading-5">ไม่พบข้อมูลสัญญา</p>
        ) : (
          <>
            <h2 className="text-16 text-field-label leading-6 font-medium">
              ที่อยู่จัดส่งเอกสารปัจจุบัน
            </h2>

            <CurrentAddressCard
              current={current}
              expanded={expanded}
              onToggle={() => {
                setExpanded((open) => !open);
              }}
            />

            <div className="bg-warning-banner mt-3 rounded-[8px] px-3 py-2 text-[12px] leading-[18px]">
              <p className="font-medium text-[#D00200]">
                กรุณาระบุที่อยู่ที่คุณสามารถรับเอกสารได้ด้วยตนเอง
              </p>
              <p className="font-light text-[#FF1512] mt-1">
                &nbsp; &nbsp; &nbsp; &nbsp; เอกสารที่บริษัทจัดส่งอาจมีข้อมูลที่เกี่ยวกับสัญญาและยอดหนี้ของคุณ
                การระบุที่อยู่ของผู้อื่นอาจทำให้บุคคลอื่นล่วงรู้ข้อมูลดังกล่าวซึ่งบริษัทไม่อาจรับผิดชอบได้
              </p>
            </div>

            <h2 className="text-16 text-field-label mt-5 leading-6 font-medium">
              ที่อยู่จัดส่งเอกสารใหม่
            </h2>

            <div className="mt-2 flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="บ้านเลขที่"
                  required
                  maxLength={16}
                  value={form.houseNo}
                  error={touched.houseNo === true ? houseNoError : ""}
                  onChange={(value) => {
                    setField("houseNo", value);
                  }}
                  onBlur={() => {
                    markBlurred("houseNo");
                  }}
                />
                <Field
                  label="หมู่บ้าน/อาคาร"
                  maxLength={500}
                  value={form.village}
                  error={touched.village === true ? villageError : ""}
                  onChange={(value) => {
                    setField("village", value);
                  }}
                  onBlur={() => {
                    markBlurred("village");
                  }}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="หมู่ที่"
                  required
                  maxLength={3}
                  value={form.moo}
                  error={touched.moo === true ? mooError : ""}
                  onChange={(value) => {
                    setField("moo", value.replace(/[^0-9-]/g, "").slice(0, 3));
                  }}
                  onBlur={() => {
                    markBlurred("moo");
                  }}
                />
                <Field
                  label="ถนน"
                  required
                  maxLength={300}
                  value={form.road}
                  error={touched.road === true ? roadError : ""}
                  onChange={(value) => {
                    setField("road", value);
                  }}
                  onBlur={() => {
                    markBlurred("road");
                  }}
                />
              </div>

              <Field
                label="ตรอก/ซอย"
                maxLength={200}
                value={form.soi}
                error={touched.soi === true ? soiError : ""}
                onChange={(value) => {
                  setField("soi", value);
                }}
                onBlur={() => {
                  markBlurred("soi");
                }}
              />
              <LocationSearchField
                label="ค้นหาจังหวัด"
                required
                value={form.province}
                onSelect={applyLocation}
              />

              <div className="grid grid-cols-2 gap-3">
                <Field label="เขต/อำเภอ" required readOnly value={form.district} />
                <Field label="แขวง/ตำบล" required readOnly value={form.subdistrict} />
              </div>

              <Field
                label="รหัสไปรษณีย์"
                required
                readOnly
                inputMode="numeric"
                value={form.postcode}
              />

              <Field
                label="เบอร์โทรศัพท์"
                required
                inputMode="numeric"
                maxLength={10}
                value={form.phone}
                error={shownPhoneError}
                onChange={(value) => {
                  setField("phone", value.replace(/\D/g, "").slice(0, 10));
                }}
                onBlur={() => {
                  markBlurred("phone");
                }}
              />
            </div>
          </>
        )}
      </main>

      {current === undefined ? null : (
        <div className="fixed inset-x-0 bottom-0 bg-white px-4 w-full pb-[calc(16px+env(safe-area-inset-bottom))]">
          <button
            type="button"
            disabled={!canContinue}
            onClick={() => {
              if (!canContinue) return;
              const confirmState: ChangeAddressConfirmState = {
                current,
                next: buildNextAddress(current, form),
              };
              const draft: ChangeAddressLocationState = { current, form };
              void navigate(ROUTES.settings.accountChangeAddress, {
                replace: true,
                state: draft,
              });
              void navigate(ROUTES.settings.accountChangeAddressConfirm, {
                state: confirmState,
              });
            }}
            className="text-24 h-12.75 w-full rounded-[10px] bg-primary text-white disabled:bg-light-gray-5"
          >
            ดำเนินการต่อ
          </button>
        </div>
      )}

      {showLeaveWarning ? (
        <LeaveWarningModal
          onCancel={() => {
            setShowLeaveWarning(false);
          }}
          onConfirm={() => {
            leavingRef.current = true;
            setShowLeaveWarning(false);
            goBack(navigate, location.key);
            leavingRef.current = false;
          }}
        />
      ) : null}
    </>
  );
}

function LeaveWarningModal({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="leave-warning-title"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          onCancel();
        }
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6"
    >
      <div className="relative w-full max-w-[350px] rounded-[14px] bg-white px-5 pt-6 pb-5 text-center">
        <button
          type="button"
          aria-label="ปิด"
          onClick={onCancel}
          className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center"
        >
          <CloseIcon />
        </button>

        <WarningTriangleIcon />

        <h2 id="leave-warning-title" className="text-18 mt-2 font-bold">
          คำเตือน
        </h2>
        <p className="text-14 text-field-label mt-2 leading-5 font-light">
          ข้อมูลที่กรอกไว้ยังไม่ได้บันทึก หากย้อนกลับ
          ข้อมูลจะหายไป คุณต้องการย้อนกลับหรือไม่?
        </p>
        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={onConfirm}
            className="text-16 h-11 w-full rounded-[10px] bg-light-gray-1 text-field-value"
          >
            ย้อนกลับ
          </button>
          <button
            type="button"
            autoFocus
            onClick={onCancel}
            className="text-16 h-11 w-full rounded-[10px] bg-primary text-white"
          >
            อยู่ต่อ
          </button>
        </div>
      </div>
    </div>
  );
}

function WarningTriangleIcon() {
  return (
    <svg viewBox="0 0 48 48" className="mx-auto h-12 w-12 shrink-0" aria-hidden="true">
      <path d="M24 6 44 42H4Z" className="fill-error" strokeLinejoin="round" />
      <rect x="21.5" y="18" width="5" height="12" rx="1.5" fill="white" />
      <circle cx="24" cy="34" r="2.5" fill="white" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 text-field-label" aria-hidden="true">
      <path
        d="M6 6 18 18M18 6 6 18"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CurrentAddressCard({
  current,
  expanded,
  onToggle,
}: {
  current: CustomerAddress;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="bg-address-card mt-2 rounded-[10px] px-3 py-3">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 text-left"
      >
        <span className="text-16 text-field-value leading-6 font-medium">
          {`เลขที่สัญญา : ${current.accountNo}`}
        </span>
        {expanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
      </button>

      {expanded ? (
        <div className="mt-3">
          <p className="text-16 text-field-label leading-6 font-medium">ที่อยู่</p>
          <p
            className="text-16 text-field-value mt-1 leading-[22.6px] font-medium"
            data-selectable="true"
          >
            {formatCustomerAddress(current)}
          </p>
          <hr className="mt-3 border-t border-divider-strong" />
          <p className="text-16 text-field-label mt-3 leading-6 font-medium">
            เบอร์โทรจัดส่งเอกสาร
          </p>
          <p
            className="text-16 text-field-value mt-1 leading-6 font-medium"
            data-selectable="true"
          >
            {pickCustomerPhone(current)}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function Field({
  label,
  required = false,
  value,
  onChange,
  onBlur,
  inputMode,
  readOnly = false,
  maxLength,
  error = "",
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  inputMode?: "text" | "numeric" | "tel";
  readOnly?: boolean;
  maxLength?: number;
  error?: string;
}) {
  const id = `field-${label}`;
  return (
    <label htmlFor={id} className="block">
      <span className="text-14 text-field-label mb-1 block">
        {label}
        {required ? (
          <span className="text-primary" aria-hidden="true">
            {" "}
            *
          </span>
        ) : null}
        {required ? <span className="sr-only"> (จำเป็น)</span> : null}
      </span>
      <input
        id={id}
        type="text"
        inputMode={inputMode}
        value={value}
        readOnly={readOnly}
        maxLength={maxLength}
        onChange={(event) => {
          onChange?.(event.target.value);
        }}
        onBlur={onBlur}
        className={`text-16 bg-secondary h-11 w-full rounded-[10px] border px-3 text-field-value outline-none ${
          error.length === 0 ? "border-input-field" : "border-error"
        }`}
      />
      {error.length === 0 ? null : (
        <p className="text-error mt-1 text-[12px] leading-[18px]">{error}</p>
      )}
    </label>
  );
}

function ChevronRightIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-field-label" aria-hidden="true">
      <path
        d="m9 5 7 7-7 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-field-label" aria-hidden="true">
      <path
        d="m5 9 7 7 7-7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
